package com.swetdot.swetgram.protocol.dto

import com.swetdot.swetgram.protocol.Timestamp
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/** Effective Swetgram Black state returned by `GET /me/premium`. */
@Serializable
enum class PremiumStatus {
    @SerialName("free") FREE,
    @SerialName("pending") PENDING,
    @SerialName("active") ACTIVE,
    @SerialName("grace_period") GRACE_PERIOD,
    @SerialName("on_hold") ON_HOLD,
    @SerialName("paused") PAUSED,
    @SerialName("expired") EXPIRED,
    @SerialName("revoked") REVOKED,
    @SerialName("unknown") UNKNOWN,
}

/**
 * Every product limit needed to render and enforce the current tier.
 *
 * These values deliberately arrive from the server instead of being reconstructed from
 * [PremiumStatus]. That lets a future regional or grandfathered plan keep its promised limits
 * without requiring an installed client update.
 */
@Serializable
data class PremiumLimits(
    @SerialName("pinned_chats") val pinnedChats: Int,
    @SerialName("chat_folders") val chatFolders: Int,
    @SerialName("accounts") val accounts: Int,
    @SerialName("bio_characters") val bioCharacters: Int,
    @SerialName("profile_links") val profileLinks: Int,
    @SerialName("profile_gallery_photos") val profileGalleryPhotos: Int,
    @SerialName("voice_message_seconds") val voiceMessageSeconds: Int,
    @SerialName("file_bytes") val fileBytes: Long,
    @SerialName("storage_bytes") val storageBytes: Long,
    @SerialName("scheduled_messages") val scheduledMessages: Int,
    @SerialName("premium_group_styles") val premiumGroupStyles: Int,
    @SerialName("discover_analytics_days") val discoverAnalyticsDays: Int,
)

/**
 * The authoritative entitlement snapshot.
 *
 * [active] is the feature-access decision. Clients must not infer access from [status] or compare
 * timestamps with a device clock. [salesEnabled] controls only starting a new purchase and never
 * overrides already verified access.
 */
@Serializable
data class PremiumEntitlement(
    @SerialName("status") val status: PremiumStatus,
    @SerialName("active") val active: Boolean,
    @SerialName("eligible") val eligible: Boolean,
    @SerialName("sales_enabled") val salesEnabled: Boolean,
    @SerialName("product_id") val productId: String,
    @SerialName("base_plan_id") val basePlanId: String? = null,
    @SerialName("current_period_end") val currentPeriodEnd: Timestamp? = null,
    @SerialName("trial_end") val trialEnd: Timestamp? = null,
    @SerialName("auto_renewing") val autoRenewing: Boolean,
    @SerialName("badge_visible") val badgeVisible: Boolean,
    @SerialName("limits") val limits: PremiumLimits,
    @SerialName("version") val version: Int,
)

/** A Play purchase token is accepted only by the authenticated verification endpoint. */
@Serializable
data class GooglePlayPurchaseRequest(
    @SerialName("product_id") val productId: String,
    @SerialName("purchase_token") val purchaseToken: String,
)

@Serializable
data class UpdatePremiumPreferencesRequest(
    @SerialName("badge_visible") val badgeVisible: Boolean,
)
